import { useState, useEffect } from "react";
import { Sidebar, Menu, MenuItem } from "react-pro-sidebar";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { tokens } from "../../theme";

// Icons
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import BusinessIcon from "@mui/icons-material/Business"; // Company
import CategoryIcon from "@mui/icons-material/Category"; // Course Categories
import SchoolIcon from "@mui/icons-material/School"; // Professional Skills
import PsychologyIcon from "@mui/icons-material/Psychology"; // Soft Skills
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import PeopleIcon from "@mui/icons-material/People"; // Users
import SettingsIcon from "@mui/icons-material/Settings"; // Settings

const Item = ({ title, to, icon, selected, setSelected }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const location = useLocation();

  const isActive = location.pathname === to || selected === title;

  return (
    <MenuItem
      active={isActive}
      style={{
        color: isActive ? colors.blueAccent[400] : colors.grey[100],
        backgroundColor: isActive ? colors.primary[600] : "transparent",

        transition: "all 0.3s ease",
      }}
      onClick={() => setSelected(title)}
      icon={icon}
      component={<Link to={to} />}
    >
      <Typography variant="body1">{title}</Typography>
    </MenuItem>
  );
};

const SidebarComponent = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selected, setSelected] = useState("Dashboard");
  const location = useLocation();

  // Get user role from localStorage or context
  const userRole = localStorage.getItem("role") || "Admin";

  // Set initial selected based on current path
  useEffect(() => {
    const path = location.pathname;
    if (path === "/") setSelected("Dashboard");
    else if (path === "/companies") setSelected("Manage Company");
    else if (path === "/course-categories")
      setSelected("Manage Course Categories");
    else if (path === "/professional-skills")
      setSelected("Manage Professional Skills");
    else if (path === "/soft-skills") setSelected("Manage Soft Skills");
    else if (path === "/users") setSelected("Manage Users");
    else if (path === "/settings") setSelected("Settings");
  }, [location.pathname]);

  return (
    <Box
      sx={{
        "& .pro-sidebar-inner": {
          background: `${colors.primary[400]} !important`,
          borderRight: "none",
        },
        "& .pro-icon-wrapper": {
          backgroundColor: "transparent !important",
        },
        "& .pro-inner-item": {
          padding: "8px 35px 8px 20px !important",
          transition: "all 0.3s ease",
        },
        "& .pro-inner-item:hover": {
          color: `${colors.blueAccent[400]} !important`,
          backgroundColor: `${colors.primary[600]} !important`,
        },
        "& .pro-menu-item.active": {
          color: `${colors.blueAccent[400]} !important`,
          backgroundColor: `${colors.primary[600]} !important`,
        },
        height: "100%",
        position: "sticky",
        top: 0,
        backgroundColor: colors.primary[400],
      }}
    >
      <Sidebar
        collapsed={isCollapsed}
        width="250px" // width when expanded
        collapsedWidth="80px" // width when collapsed
        rootStyles={{
          height: "100%",
          backgroundColor: colors.primary[400],
        }}
      >
        <Menu
          iconShape="square"
          style={{ flex: 1, display: "flex", flexDirection: "column" }}
        >
          {/* LOGO AND MENU ICON */}
          <MenuItem
            onClick={() => setIsCollapsed(!isCollapsed)}
            icon={isCollapsed ? <MenuOutlinedIcon /> : undefined}
            style={{
              margin: "10px 0 20px 0",
              color: colors.grey[100],
            }}
          >
            {!isCollapsed && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                ml="15px"
              >
                <Typography
                  variant="h3"
                  color={colors.grey[100]}
                  fontWeight="bold"
                >
                  ADMINIS
                </Typography>
                <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                  <MenuOutlinedIcon />
                </IconButton>
              </Box>
            )}
          </MenuItem>

          {/* USER INFO */}
          {!isCollapsed && (
            <Box mb="25px" textAlign="center">
              <Box
                sx={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  backgroundColor: colors.greenAccent[500],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 10px",
                  fontSize: "2rem",
                  fontWeight: "bold",
                  color: colors.grey[100],
                }}
              >
                ER
              </Box>
              <Typography
                variant="h4"
                color={colors.grey[100]}
                fontWeight="bold"
                sx={{ m: "10px 0 5px 0" }}
              >
                Ed Roh
              </Typography>
              <Typography variant="body2" color={colors.greenAccent[500]}>
                {userRole === "Admin"
                  ? "System Administrator"
                  : "Standard User"}
              </Typography>
            </Box>
          )}

          {/* Navigation Items */}
          <Box paddingLeft={isCollapsed ? undefined : "10%"} py="10px">
            <Item
              title="Dashboard"
              to="/"
              icon={<HomeOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            />

            {/* Admin Only Sections */}
            {userRole === "Admin" && (
              <>
                {/* Company Section */}
                {!isCollapsed && (
                  <Typography
                    variant="overline"
                    color={colors.grey[300]}
                    sx={{
                      m: "15px 0 5px 20px",
                      display: "block",
                      fontWeight: "bold",
                      letterSpacing: "1px",
                    }}
                  >
                    Company
                  </Typography>
                )}
                <Item
                  title="Manage Company"
                  to="/companies"
                  icon={<BusinessIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />

                {/* Courses Section */}
                {!isCollapsed && (
                  <Typography
                    variant="overline"
                    color={colors.grey[300]}
                    sx={{
                      m: "15px 0 5px 20px",
                      display: "block",
                      fontWeight: "bold",
                      letterSpacing: "1px",
                    }}
                  >
                    Courses
                  </Typography>
                )}

                <Item
                  title="Course Categories"
                  to="/course-categories"
                  icon={<CategoryIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />

                <Item
                  title="Professional Skills"
                  to="/professional-skills"
                  icon={<SchoolIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />

                <Item
                  title="Soft Skills"
                  to="/soft-skills"
                  icon={<PsychologyIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />

                {/* Users Management */}
                {!isCollapsed && (
                  <Typography
                    variant="overline"
                    color={colors.grey[300]}
                    sx={{
                      m: "15px 0 5px 20px",
                      display: "block",
                      fontWeight: "bold",
                      letterSpacing: "1px",
                    }}
                  >
                    Management
                  </Typography>
                )}
                <Item
                  title="Manage Users"
                  to="/users"
                  icon={<PeopleIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
              </>
            )}

            {/* Settings (Available for all users) */}
            {!isCollapsed && (
              <Typography
                variant="overline"
                color={colors.grey[300]}
                sx={{
                  m: "15px 0 5px 20px",
                  display: "block",
                  fontWeight: "bold",
                  letterSpacing: "1px",
                }}
              >
                System
              </Typography>
            )}
            <Item
              title="Settings"
              to="/settings"
              icon={<SettingsIcon />}
              selected={selected}
              setSelected={setSelected}
            />
          </Box>

          {/* Sidebar Footer */}
          {!isCollapsed && (
            <Box
              sx={{
                textAlign: "center",
                py: 1,
                borderTop: `1px solid ${colors.primary[300]}`,
                color: colors.grey[500],
              }}
            >
              <Typography variant="caption">
                v1.0.0 • © 2024 ADMINIS
              </Typography>
            </Box>
          )}
        </Menu>
      </Sidebar>
    </Box>
  );
};

export default SidebarComponent;
